import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import IconCalendar from '@assets/images/icon_calendar.svg';
import { Dropdown } from '@components/common/Dropdown';
import { media } from '@styles/mediaQuery';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

export enum DateRangePreset {
  CUSTOM = '직접 선택',
  TODAY = '오늘',
  YESTERDAY = '어제',
  THIS_WEEK = '이번 주',
  MONTH = '이번 달',
  LAST_MONTH = '지난달',
  PREV_MONTH = '1개월',
  QUARTER_YEAR = '3개월',
  HALF_YEAR = '6개월',
  YEAR = '1년',
}

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (event: { target: { name: string; value: string } }) => void;
  defaultPreset?: DateRangePreset;
  maxPeriod?: DateRangePreset;
  disabled?: boolean;
}

export const PRESET_OPTIONS = [
  { value: DateRangePreset.CUSTOM, label: '직접 선택' },
  { value: DateRangePreset.TODAY, label: '오늘' },
  { value: DateRangePreset.YESTERDAY, label: '어제' },
  { value: DateRangePreset.THIS_WEEK, label: '이번 주' },
  { value: DateRangePreset.MONTH, label: '이번 달' },
  { value: DateRangePreset.LAST_MONTH, label: '지난달' },
  { value: DateRangePreset.PREV_MONTH, label: '1개월' },
  { value: DateRangePreset.QUARTER_YEAR, label: '3개월' },
  { value: DateRangePreset.HALF_YEAR, label: '6개월' },
  { value: DateRangePreset.YEAR, label: '1년' },
];

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const parseDateValue = (value: string): Date | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

// 시작일/종료일 텍스트 입력 파싱: "2026-07-05", "2026.07.05", "2026/07/05", "20260705" 허용
const parseFlexibleDate = (value: string): Date | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = /^\d{8}$/.test(trimmed)
    ? `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`
    : trimmed.replace(/[./]/g, '-');

  if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(normalized)) return null;

  const parsed = dayjs(normalized);
  return parsed.isValid() ? parsed.toDate() : null;
};

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return dayjs(a).isSame(dayjs(b), 'day');
};

const isDayBetween = (day: Date, start: Date, end: Date) => {
  const d = dayjs(day);
  const [lo, hi] = dayjs(start).isAfter(dayjs(end), 'day') ? [end, start] : [start, end];
  return d.isAfter(dayjs(lo), 'day') && d.isBefore(dayjs(hi), 'day');
};

interface CalendarCell {
  date: Dayjs;
  inMonth: boolean;
}

const buildCalendarCells = (month: Dayjs): CalendarCell[] => {
  const startOfMonth = month.startOf('month');
  const daysInMonth = month.daysInMonth();
  const startWeekday = startOfMonth.day();
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;

  return Array.from({ length: totalCells }, (_, i) => {
    const date = startOfMonth.add(i - startWeekday, 'day');
    return { date, inMonth: date.month() === month.month() };
  });
};

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  defaultPreset,
  maxPeriod,
  disabled,
}: DateRangePickerProps) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    parseDateValue(startDate),
    parseDateValue(endDate),
  ]);
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(
    defaultPreset || DateRangePreset.CUSTOM,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Dayjs>(dayjs());
  const [draftStart, setDraftStart] = useState<Date | null>(null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState<number>(dayjs().year());
  const [startInputText, setStartInputText] = useState('');
  const [endInputText, setEndInputText] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const visibleOptions = maxPeriod
    ? PRESET_OPTIONS.slice(0, PRESET_OPTIONS.findIndex((opt) => opt.value === maxPeriod) + 1)
    : PRESET_OPTIONS;

  const getPresetDates = (preset: DateRangePreset): [Date, Date] | null => {
    const today = dayjs();
    let start: Date;
    let end: Date;

    switch (preset) {
      case DateRangePreset.CUSTOM:
        return null;
      case DateRangePreset.TODAY:
        start = today.startOf('day').toDate();
        end = today.endOf('day').toDate();
        break;
      case DateRangePreset.YESTERDAY:
        start = today.subtract(1, 'day').startOf('day').toDate();
        end = today.subtract(1, 'day').endOf('day').toDate();
        break;
      case DateRangePreset.THIS_WEEK:
        start = today.startOf('week').toDate();
        end = today.endOf('week').toDate();
        break;
      case DateRangePreset.MONTH:
        start = today.startOf('month').toDate();
        end = today.endOf('month').toDate();
        break;
      case DateRangePreset.LAST_MONTH:
        start = today.subtract(1, 'month').startOf('month').toDate();
        end = today.startOf('month').subtract(1, 'day').toDate();
        break;
      case DateRangePreset.PREV_MONTH:
        // 1개월 전 다음 날부터 오늘까지
        start = today.subtract(1, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.QUARTER_YEAR:
        // 3개월 전 다음 날부터 오늘까지
        start = today.subtract(3, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.HALF_YEAR:
        // 6개월 전 다음 날부터 오늘까지
        start = today.subtract(6, 'month').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      case DateRangePreset.YEAR:
        // 1년 전 다음 날부터 오늘까지
        start = today.subtract(1, 'year').add(1, 'day').startOf('day').toDate();
        end = today.toDate();
        break;
      default:
        start = today.toDate();
        end = today.toDate();
    }

    return [start, end];
  };

  // CustomEvent 기반 필터 초기화 리스너
  useEffect(() => {
    const handleReset = () => {
      if (defaultPreset) {
        const dates = getPresetDates(defaultPreset);
        if (dates) {
          const [start, end] = dates;
          setDateRange([start, end]);
          onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
          onChange({ target: { name: 'endDate', value: dayjs(end).format('YYYY-MM-DD') } });
        }
        setSelectedPreset(defaultPreset);
      }
    };

    window.addEventListener('reset-filter', handleReset);
    return () => window.removeEventListener('reset-filter', handleReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPreset]);

  // 팝업 바깥 클릭 시 선택 미완료 상태로 닫힘(취소)
  // mousedown을 사용: click은 React의 상태 업데이트(예: 월 선택 시 해당 버튼 자체가
  // 언마운트되는 경우)로 event.target이 DOM에서 분리된 뒤 도달할 수 있어
  // containerRef.contains() 판정이 깨진다. mousedown은 그 전에 먼저 발생한다.
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setDraftStart(null);
        setDraftEnd(null);
        setHoverDate(null);
        setIsMonthPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // 시작일/종료일 텍스트 입력 필드를 캘린더 선택 상태와 동기화
  useEffect(() => {
    if (!isOpen) return;
    const s = draftStart || dateRange[0];
    const e = draftEnd || (draftStart ? null : dateRange[1]);
    setStartInputText(s ? dayjs(s).format('YYYY-MM-DD') : '');
    setEndInputText(e ? dayjs(e).format('YYYY-MM-DD') : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, draftStart, draftEnd]);

  // maxPeriod에 따른 최대 일수 계산
  const getMaxDays = (): number | null => {
    if (!maxPeriod) return null;

    switch (maxPeriod) {
      case DateRangePreset.TODAY:
      case DateRangePreset.YESTERDAY:
        return 1;
      case DateRangePreset.THIS_WEEK:
        return 7;
      case DateRangePreset.MONTH:
        return 31;
      case DateRangePreset.LAST_MONTH:
        return 31;
      case DateRangePreset.PREV_MONTH:
        return 31;
      case DateRangePreset.QUARTER_YEAR:
        return 93;
      case DateRangePreset.HALF_YEAR:
        return 186;
      case DateRangePreset.YEAR:
        return 365;
      default:
        return null;
    }
  };

  const formatDisplayDate = (date: Date | null) => {
    if (!date || !dayjs(date).isValid()) return '';
    return dayjs(date).format('YYYY.MM.DD');
  };

  const emitDateRange = (start: Date, end: Date) => {
    let nextStart = start;
    let nextEnd = end;

    if (dayjs(nextEnd).isBefore(dayjs(nextStart), 'day')) {
      [nextStart, nextEnd] = [nextEnd, nextStart];
    }

    if (maxPeriod) {
      const maxDays = getMaxDays();
      if (maxDays) {
        const diffDays = dayjs(nextEnd).diff(dayjs(nextStart), 'day');
        if (diffDays > maxDays) {
          nextEnd = dayjs(nextStart).add(maxDays, 'day').toDate();
          toast.info(`선택한 기간이 최대 범위(${maxDays}일)를 초과하여 자동으로 조정되었습니다.`, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      }
    }

    setDateRange([nextStart, nextEnd]);
    setSelectedPreset(DateRangePreset.CUSTOM);
    onChange({ target: { name: 'startDate', value: dayjs(nextStart).format('YYYY-MM-DD') } });
    onChange({ target: { name: 'endDate', value: dayjs(nextEnd).format('YYYY-MM-DD') } });
  };

  // 프리셋 선택 핸들러
  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as DateRangePreset;
    if (!value) return;

    setSelectedPreset(value);

    if (value === DateRangePreset.CUSTOM) {
      return;
    }

    const dates = getPresetDates(value);
    if (dates) {
      const [start, end] = dates;
      setDateRange([start, end]);
      onChange({ target: { name: 'startDate', value: dayjs(start).format('YYYY-MM-DD') } });
      onChange({ target: { name: 'endDate', value: dayjs(end).format('YYYY-MM-DD') } });
    }
  };

  const decidePopoverAlign = () => {
    if (!triggerRef.current) return;
    const POPOVER_WIDTH = 480;
    const rect = triggerRef.current.getBoundingClientRect();
    const overflowsRight = rect.left + POPOVER_WIDTH > window.innerWidth;
    setPopoverAlign(overflowsRight ? 'right' : 'left');
  };

  const handleTriggerClick = () => {
    if (disabled) return;

    if (isOpen) {
      setIsOpen(false);
      setDraftStart(null);
      setDraftEnd(null);
      setHoverDate(null);
      setIsMonthPickerOpen(false);
      return;
    }

    const [start] = dateRange;
    setDraftStart(null);
    setDraftEnd(null);
    setHoverDate(null);
    setIsMonthPickerOpen(false);
    setViewMonth(dayjs(start || new Date()));
    decidePopoverAlign();
    setIsOpen(true);
  };

  const handleDayClick = (day: Dayjs) => {
    const clicked = day.toDate();

    if (draftStart && !draftEnd) {
      const finalStart = draftStart;
      setDraftStart(null);
      setDraftEnd(null);
      setHoverDate(null);
      setIsOpen(false);
      emitDateRange(finalStart, clicked);
      return;
    }

    setDraftStart(clicked);
    setDraftEnd(null);
    setHoverDate(null);
  };

  const goPrevMonth = () => setViewMonth((m) => m.subtract(1, 'month'));
  const goNextMonth = () => setViewMonth((m) => m.add(1, 'month'));

  const toggleMonthPicker = () => {
    setPickerYear(viewMonth.year());
    setIsMonthPickerOpen((prev) => !prev);
  };

  const handlePickMonth = (month: number) => {
    setViewMonth(dayjs(`${pickerYear}-${String(month).padStart(2, '0')}-01`));
    setIsMonthPickerOpen(false);
  };

  const commitStartInput = () => {
    const parsed = parseFlexibleDate(startInputText);
    if (!parsed) {
      const s = draftStart || dateRange[0];
      setStartInputText(s ? dayjs(s).format('YYYY-MM-DD') : '');
      return;
    }
    setDraftStart(parsed);
    setDraftEnd(null);
    setHoverDate(null);
    setViewMonth(dayjs(parsed));
  };

  const commitEndInput = () => {
    const parsed = parseFlexibleDate(endInputText);
    if (!parsed) {
      const e = draftEnd || (draftStart ? null : dateRange[1]);
      setEndInputText(e ? dayjs(e).format('YYYY-MM-DD') : '');
      return;
    }
    const anchor = draftStart || dateRange[0] || parsed;
    setDraftStart(null);
    setDraftEnd(null);
    setHoverDate(null);
    setIsOpen(false);
    emitDateRange(anchor, parsed);
  };

  const today = dayjs();
  const previewEnd = draftStart && !draftEnd ? hoverDate : null;

  const renderMonthGrid = (monthToShow: Dayjs) => {
    const cells = buildCalendarCells(monthToShow);
    return (
      <>
        <WeekdayRow>
          {WEEKDAY_LABELS.map((label) => (
            <WeekdayCell key={label}>{label}</WeekdayCell>
          ))}
        </WeekdayRow>

        <CalendarGrid>
          {cells.map(({ date, inMonth }) => {
            const cellDate = date.toDate();
            const isStart = isSameDay(cellDate, draftStart);
            const isEnd = isSameDay(cellDate, draftEnd);
            const isSelectedBoundary = isStart || isEnd;
            const isInRange =
              draftStart && draftEnd
                ? isDayBetween(cellDate, draftStart, draftEnd)
                : draftStart && previewEnd
                  ? isDayBetween(cellDate, draftStart, previewEnd)
                  : false;
            const isPreviewBoundary = draftStart && previewEnd && isSameDay(cellDate, previewEnd);
            const isToday = date.isSame(today, 'day');

            return (
              <DayCell
                key={date.format('YYYY-MM-DD')}
                type="button"
                disabled={!inMonth}
                $inMonth={inMonth}
                $selected={isSelectedBoundary || Boolean(isPreviewBoundary)}
                $inRange={Boolean(isInRange)}
                $today={isToday}
                onClick={() => inMonth && handleDayClick(date)}
                onMouseEnter={() => inMonth && draftStart && !draftEnd && setHoverDate(cellDate)}>
                {date.date()}
              </DayCell>
            );
          })}
        </CalendarGrid>
      </>
    );
  };

  return (
    <InputWrapper ref={containerRef}>
      <DropdownWrapper $disabled={disabled}>
        <Dropdown>
          <select onChange={handlePresetChange} disabled={disabled} value={selectedPreset}>
            {visibleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Dropdown>
      </DropdownWrapper>

      <CalendarTrigger>
        <TriggerButton
          type="button"
          ref={triggerRef}
          disabled={disabled}
          $active={isOpen}
          onClick={handleTriggerClick}>
          <img src={IconCalendar} alt="" width={16} height={16} />
          <span>
            {dateRange[0] || dateRange[1]
              ? `${formatDisplayDate(dateRange[0])} ~ ${formatDisplayDate(dateRange[1])}`
              : '기간을 선택하세요'}
          </span>
        </TriggerButton>

        {isOpen && (
          <CalendarPopover $align={popoverAlign}>
            <DateInputRow>
              <DateTextField>
                <DateTextLabel>시작일</DateTextLabel>
                <DateTextInput
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY-MM-DD"
                  value={startInputText}
                  onChange={(e) => setStartInputText(e.target.value)}
                  onBlur={commitStartInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitStartInput();
                    }
                  }}
                />
              </DateTextField>
              <DateInputDivider>~</DateInputDivider>
              <DateTextField>
                <DateTextLabel>종료일</DateTextLabel>
                <DateTextInput
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY-MM-DD"
                  value={endInputText}
                  onChange={(e) => setEndInputText(e.target.value)}
                  onBlur={commitEndInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEndInput();
                    }
                  }}
                />
              </DateTextField>
            </DateInputRow>

            {isMonthPickerOpen ? (
              <MonthPicker>
                <YearNav>
                  <NavButton
                    type="button"
                    onClick={() => setPickerYear((y) => y - 1)}
                    aria-label="이전 연도">
                    ◀
                  </NavButton>
                  <span>{pickerYear}년</span>
                  <NavButton
                    type="button"
                    onClick={() => setPickerYear((y) => y + 1)}
                    aria-label="다음 연도">
                    ▶
                  </NavButton>
                </YearNav>
                <MonthGrid>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <MonthCell
                      key={month}
                      type="button"
                      $active={pickerYear === viewMonth.year() && month === viewMonth.month() + 1}
                      onClick={() => handlePickMonth(month)}>
                      {month}월
                    </MonthCell>
                  ))}
                </MonthGrid>
              </MonthPicker>
            ) : (
              <CalendarBody>
                <CalendarPanel>
                  <PanelHeader>
                    <NavButton type="button" onClick={goPrevMonth} aria-label="이전 달">
                      ◀
                    </NavButton>
                    <MonthYearButton type="button" onClick={toggleMonthPicker}>
                      {viewMonth.format('YYYY년 M월')} ▾
                    </MonthYearButton>
                  </PanelHeader>
                  {renderMonthGrid(viewMonth)}
                </CalendarPanel>

                <CalendarPanel>
                  <PanelHeader>
                    <MonthLabel>{viewMonth.add(1, 'month').format('YYYY년 M월')}</MonthLabel>
                    <NavButton type="button" onClick={goNextMonth} aria-label="다음 달">
                      ▶
                    </NavButton>
                  </PanelHeader>
                  {renderMonthGrid(viewMonth.add(1, 'month'))}
                </CalendarPanel>
              </CalendarBody>
            )}
          </CalendarPopover>
        )}
      </CalendarTrigger>
    </InputWrapper>
  );
};

export default DateRangePicker;

// Styled Components

const InputWrapper = styled.div`
  width: 100%;
  max-width: 31rem;
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  flex-wrap: wrap;

  > * {
    min-width: 0;
  }
`;

const DropdownWrapper = styled.div<{ $disabled?: boolean }>`
  flex-shrink: 0;
  width: 5rem;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

const CalendarTrigger = styled.div`
  position: relative;
  flex: 1;
  min-width: 19rem;

  @media (max-width: 520px) {
    min-width: 100%;
  }
`;

const TriggerButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  height: 2.25rem;
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.primary : theme.neutralGray[300])};
  border-radius: 0.25rem;
  background: #fff;
  color: ${({ theme }) => theme.neutralGray[700]};
  font-size: 0.875rem;
  cursor: pointer;
  transition: border-color 0.2s ease;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CalendarPopover = styled.div<{ $align: 'left' | 'right' }>`
  position: absolute;
  top: calc(100% + 0.35rem);
  ${({ $align }) => ($align === 'right' ? 'right: 0;' : 'left: 0;')}
  z-index: 60;
  width: 29rem;
  padding: 0.75rem;
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.neutralGray[200]};
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(15, 42, 74, 0.12);

  ${media.mobile`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100%;
    max-height: 85vh;
    overflow-y: auto;
    border-radius: 0.75rem 0.75rem 0 0;
    box-shadow: 0 -4px 16px rgba(15, 42, 74, 0.16);
  `}
`;

const DateInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
`;

const DateTextField = styled.label`
  flex: 1;
  min-width: 0;
  height: 2.25rem;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid ${({ theme }) => theme.neutralGray[300]};
  border-radius: 0.25rem;

  &:focus-within {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const DateTextLabel = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 0.75rem;
`;

const DateTextInput = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: ${({ theme }) => theme.neutralGray[700]};
  font-size: 0.8125rem;
`;

const DateInputDivider = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 0.8125rem;
`;

const CalendarBody = styled.div`
  display: flex;
  gap: 0.75rem;

  ${media.mobile`
    flex-direction: column;
  `}
`;

const CalendarPanel = styled.div`
  flex: 1;
  min-width: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.neutralGray[600]};
`;

const MonthLabel = styled.span`
  padding: 0.25rem 0.5rem;
`;

const MonthYearButton = styled.button`
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;

  &:hover {
    background: ${({ theme }) => theme.neutralGray[100]};
  }
`;

const MonthPicker = styled.div`
  padding-top: 0.25rem;
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.neutralGray[600]};
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.375rem;
`;

const MonthCell = styled.button<{ $active: boolean }>`
  height: 2.5rem;
  border: none;
  border-radius: 0.25rem;
  background: ${({ theme, $active }) => ($active ? theme.primary : theme.neutralGray[100])};
  color: ${({ theme, $active }) => ($active ? theme.white : theme.neutralGray[600])};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.primary : theme.main[200])};
  }
`;

const NavButton = styled.button`
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: ${({ theme }) => theme.neutralGray[500]};
  cursor: pointer;
  font-size: 0.75rem;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.neutralGray[100]};
  }
`;

const WeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 0.25rem;
`;

const WeekdayCell = styled.span`
  text-align: center;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.neutralGray[400]};
  padding: 0.25rem 0;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.125rem;
`;

const DayCell = styled.button<{
  $inMonth: boolean;
  $selected: boolean;
  $inRange: boolean;
  $today: boolean;
}>`
  height: 2rem;
  border: none;
  border-radius: 0.25rem;
  background: ${({ theme, $selected, $inRange }) =>
    $selected ? theme.primary : $inRange ? theme.main[100] : 'transparent'};
  color: ${({ theme, $inMonth, $selected }) => {
    if (!$inMonth) return theme.neutralGray[300];
    if ($selected) return theme.white;
    return theme.neutralGray[600];
  }};
  font-weight: ${({ $today, $selected }) => ($today || $selected ? 700 : 400)};
  font-size: 0.75rem;
  cursor: ${({ $inMonth }) => ($inMonth ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme, $selected, $inMonth }) =>
      $selected || !$inMonth ? undefined : theme.main[200]};
  }

  &:disabled {
    cursor: default;
  }
`;
